import API from '@Services/apiAxios';
import axios from 'axios';
import { formatShipmentNumber } from '../types';

export interface PresignedUrlResponse {
  url: string;
  fields: Record<string, string>;
  key: string;
}

export interface PresignedUrlsResponse {
  batchId: string;
  files: PresignedUrlResponse[];
}

export interface VendorInvoiceCharge {
  name: string;
  amount: number;
}

export interface VendorInvoiceShipment {
  shipmentNumber?: string | null;
  awbNumber?: string | null;
  actualWeight?: number | null;
  volumetricWeight?: number | null;
  chargeableWeight?: number | null;
  unit?: string | null;
  length?: number | null;
  breadth?: number | null;
  height?: number | null;
  weight?: number | null;
  numberOfBoxes?: number | null;
  isTaxed?: boolean | string | null;
  carrierReferenceNumber?: string | null;
  charges?: VendorInvoiceCharge[];
  [key: string]: any;
}

export interface VendorInvoiceGroup {
  carrierReferenceNumber?: string | null;
  shipments?: VendorInvoiceShipment[];
  [key: string]: any;
}

export interface VendorInvoiceExtractionResponse {
  jobId?: string;
  status?: string;
  documentType?: string;
  confidence?: number;
  invalidFields?: string[];
  error?: string | null;
  fields?: {
    invoices?: VendorInvoiceGroup[];
    shipments?: VendorInvoiceShipment[];
    [key: string]: any;
  };
  [key: string]: any;
}

export interface ExtractionProcessOptions {
  onStatusChange?: (status: string) => void;
  onUploadProgress?: (percent: number) => void;
  isCancelled?: () => boolean;
  registerTimer?: (timer: NodeJS.Timeout | null) => void;
  pollIntervalMs?: number;
}

export interface StartBatchExtractionResponse {
  batchId: string;
  [key: string]: any;
}

export interface BatchJob {
  jobId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'needs_review' | string;
  confidence?: number;
  fields?: {
    invoices?: VendorInvoiceGroup[];
    shipments?: VendorInvoiceShipment[];
    [key: string]: any;
  };
  invalidFields?: any[];
  error?: string | any | null;
  [key: string]: any;
}

export interface BatchExtractionStatusResponse {
  batchId: string;
  status: string;
  counts?: {
    pending: number;
    completed: number;
    failed: number;
    needs_review: number;
  };
  jobs?: BatchJob[];
  progress?: number;
  result?: any;
  message?: string;
  error?: string | any | null;
  [key: string]: any;
}

export interface BatchExtractionProcessOptions {
  onStatusChange?: (status: string) => void;
  onUploadProgress?: (percent: number) => void;
  isCancelled?: () => boolean;
  registerTimer?: (timer: NodeJS.Timeout | null) => void;
  pollIntervalMs?: number;
}

export interface ProcessBatchVendorInvoiceExtractionResult {
  batchId: string;
  status: string;
  counts?: {
    pending: number;
    completed: number;
    failed: number;
    needs_review: number;
  };
  jobs?: BatchJob[];
  shipments: any[];
  successfulCount: number;
  failedCount: number;
  error?: string | null;
}

/**
 * Safely unwrap extraction response object whether returned at root level
 * or wrapped inside response.data.data
 */
export const unwrapExtractionResponse = (raw: any): VendorInvoiceExtractionResponse => {
  if (!raw) return {};
  if (raw.jobId || raw.status || raw.fields) {
    return raw;
  }
  if (raw.data && typeof raw.data === 'object') {
    if (raw.data.jobId || raw.data.status || raw.data.fields) {
      return {
        ...raw.data,
        error: raw.data.error || raw.error || null,
      };
    }
  }
  return raw;
};

/**
 * Safely unwrap batch extraction status response object
 */
export const unwrapBatchExtractionResponse = (raw: any): BatchExtractionStatusResponse => {
  if (!raw) return { batchId: '', status: '' };
  if (raw.batchId || raw.status || raw.jobs || raw.counts) {
    return raw;
  }
  if (raw.data && typeof raw.data === 'object') {
    if (raw.data.batchId || raw.data.status || raw.data.jobs || raw.data.counts) {
      return raw.data;
    }
  }
  return raw;
};

/**
 * Safely unwrap batch upload URLs response object
 */
export const unwrapPresignedUrlsResponse = (raw: any): PresignedUrlsResponse => {
  if (!raw) return { batchId: '', files: [] };
  if (raw.batchId && Array.isArray(raw.files)) {
    return raw;
  }
  if (raw.data && typeof raw.data === 'object' && raw.data.batchId && Array.isArray(raw.data.files)) {
    return raw.data;
  }
  return raw;
};

/**
 * Step 1: Request presigned upload URL for single vendor invoice extraction
 * POST /speedboxbot/extract/upload-url
 */
export const getUploadUrl = async (file: File): Promise<PresignedUrlResponse> => {
  const { data } = await API.post<PresignedUrlResponse>('/speedboxbot/extract/upload-url', {
    fileName: file.name,
    mimeType: file.type || 'application/octet-stream',
  });
  return data;
};

/**
 * Step 1 (Multi-file): Request presigned upload URLs for multiple vendor invoices (batch)
 * POST /speedboxbot/extract/upload-urls
 */
export const getBatchUploadUrls = async (files: File[]): Promise<PresignedUrlsResponse> => {
  const { data } = await API.post<PresignedUrlsResponse>('/speedboxbot/extract/upload-urls', {
    files: files.map(file => ({
      fileName: file.name,
      mimeType: file.type || 'application/octet-stream',
    })),
  });
  const unwrapped = unwrapPresignedUrlsResponse(data);
  if (!unwrapped || !unwrapped.batchId || !Array.isArray(unwrapped.files)) {
    throw new Error('Invalid response received from upload-urls endpoint');
  }
  if (unwrapped.files.length !== files.length) {
    throw new Error('A different number of upload URLs than requested was returned');
  }
  return unwrapped;
};

/**
 * Step 2: Upload file directly to S3 bucket via presigned POST
 */
export const uploadToS3 = async (
  uploadUrlResponse: PresignedUrlResponse,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<void> => {
  const { url, fields } = uploadUrlResponse;
  const formData = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    formData.append(key, value);
  });
  formData.append('file', file);

  await axios.post(url, formData, {
    onUploadProgress: progressEvent => {
      if (onProgress) {
        const percent = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
        onProgress(percent);
      }
    },
  });
};

/**
 * Step 3: Trigger vendor invoice document extraction with documentType = "vendor_invoice" and mode = "async"
 * POST /speedboxbot/extract
 */
export const extractVendorInvoiceDocument = async (s3Key: string): Promise<VendorInvoiceExtractionResponse> => {
  const { data } = await API.post<VendorInvoiceExtractionResponse>('/speedboxbot/extract', {
    documentType: 'vendor_invoice',
    s3Key,
    mode: 'async',
  });
  return unwrapExtractionResponse(data);
};

/**
 * Step 3 (Multi-file): Trigger batch vendor invoice extraction with documentType = "vendor_invoice"
 * POST /speedboxbot/extract/batch
 */
export const startBatchVendorInvoiceExtraction = async (
  batchId: string,
  files: string[],
): Promise<StartBatchExtractionResponse> => {
  const { data } = await API.post<StartBatchExtractionResponse>('/speedboxbot/extract/batch', {
    batchId,
    documentType: 'vendor_invoice',
    files,
  });
  return data;
};

/**
 * Step 4: Poll single extraction job status
 * GET /speedboxbot/extract/{jobId}
 */
export const getVendorInvoiceExtractionStatus = async (jobId: string): Promise<VendorInvoiceExtractionResponse> => {
  const { data } = await API.get<VendorInvoiceExtractionResponse>(`/speedboxbot/extract/${jobId}`);
  return unwrapExtractionResponse(data);
};

/**
 * Step 4 (Multi-file): Poll batch extraction status
 * GET /speedboxbot/extract/batch/{batchId}
 */
export const getBatchVendorInvoiceExtractionStatus = async (
  batchId: string,
): Promise<BatchExtractionStatusResponse> => {
  const { data } = await API.get<BatchExtractionStatusResponse>(`/speedboxbot/extract/batch/${batchId}`);
  return unwrapBatchExtractionResponse(data);
};

/**
 * Full 3-step AI extraction runner for single file with async polling:
 * 1. Generate presigned URL
 * 2. Upload to S3
 * 3. Initial async extraction request (mode: "async")
 * 4. Poll every 10 seconds until terminal status (completed, needs_review, failed)
 */
export const processVendorInvoiceExtraction = async (
  file: File,
  optionsOrStatusChange?: ((status: string) => void) | ExtractionProcessOptions,
  legacyOnUploadProgress?: (percent: number) => void,
): Promise<VendorInvoiceExtractionResponse> => {
  let options: ExtractionProcessOptions = {};
  if (typeof optionsOrStatusChange === 'function') {
    options = {
      onStatusChange: optionsOrStatusChange,
      onUploadProgress: legacyOnUploadProgress,
    };
  } else if (optionsOrStatusChange) {
    options = optionsOrStatusChange;
  }

  const { onStatusChange, onUploadProgress, isCancelled, registerTimer, pollIntervalMs = 10000 } = options;

  if (isCancelled?.()) throw new Error('Extraction cancelled');

  // Step 1: Upload URL
  onStatusChange?.('Generating secure upload URL...');
  const presigned = await getUploadUrl(file);

  if (isCancelled?.()) throw new Error('Extraction cancelled');

  // Step 2: Upload to S3
  onStatusChange?.('Uploading invoice document to S3...');
  const s3Key = presigned.key || presigned.fields?.['key'];
  await uploadToS3(presigned, file, onUploadProgress);

  if (isCancelled?.()) throw new Error('Extraction cancelled');

  // Step 3: Initial async extraction request
  onStatusChange?.('Submitting vendor invoice for AI extraction...');
  const initialResponse = await extractVendorInvoiceDocument(s3Key);

  // If already terminal (e.g. sync fallback response)
  const initialStatus = initialResponse.status;
  if (initialStatus === 'completed' || initialStatus === 'failed' || initialStatus === 'needs_review') {
    return initialResponse;
  }

  const jobId = initialResponse.jobId;
  if (!jobId) {
    if (initialResponse.fields && Object.keys(initialResponse.fields).length > 0) {
      return initialResponse;
    }
    return {
      status: 'failed',
      error: initialResponse.error || 'Failed to initialize extraction: missing jobId in response',
    };
  }

  // Step 4: Polling loop every 10 seconds
  let currentStatus = initialStatus || 'pending';

  while (currentStatus === 'pending' || currentStatus === 'processing') {
    if (isCancelled?.()) throw new Error('Extraction cancelled');

    onStatusChange?.(
      currentStatus === 'processing'
        ? 'AI is analyzing the invoice... (processing)'
        : 'AI extraction in progress... (waiting for results)',
    );

    // Wait 10 seconds before the poll without overlapping requests
    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        registerTimer?.(null);
        if (isCancelled?.()) {
          reject(new Error('Extraction cancelled'));
        } else {
          resolve();
        }
      }, pollIntervalMs);
      registerTimer?.(timer);
    });

    if (isCancelled?.()) throw new Error('Extraction cancelled');

    onStatusChange?.('Checking extraction status...');
    const pollResult = await getVendorInvoiceExtractionStatus(jobId);

    currentStatus = pollResult.status || 'failed';

    if (currentStatus === 'completed' || currentStatus === 'failed' || currentStatus === 'needs_review') {
      return pollResult;
    }
  }

  return {
    status: currentStatus,
    error: `Unexpected extraction status: ${currentStatus}`,
  };
};

/**
 * Multi-file batch AI extraction runner with async polling:
 * 1. Request presigned upload URLs for all files
 * 2. Upload all files to S3 in parallel (Promise.all)
 * 3. Initiate batch extraction via POST /speedboxbot/extract/batch with documentType: "vendor_invoice"
 * 4. Poll GET /speedboxbot/extract/batch/{batchId} until counts.pending === 0 or terminal status
 * 5. Aggregate extracted shipments across all completed/needs_review jobs
 */
export const processBatchVendorInvoiceExtraction = async (
  files: File[],
  optionsOrStatusChange?: ((status: string) => void) | BatchExtractionProcessOptions,
  legacyOnUploadProgress?: (percent: number) => void,
): Promise<ProcessBatchVendorInvoiceExtractionResult> => {
  let options: BatchExtractionProcessOptions = {};
  if (typeof optionsOrStatusChange === 'function') {
    options = {
      onStatusChange: optionsOrStatusChange,
      onUploadProgress: legacyOnUploadProgress,
    };
  } else if (optionsOrStatusChange) {
    options = optionsOrStatusChange;
  }

  const { onStatusChange, onUploadProgress, isCancelled, registerTimer, pollIntervalMs = 10000 } = options;

  if (isCancelled?.()) throw new Error('Extraction cancelled');

  // Step 1: Upload URLs for batch
  onStatusChange?.(`Generating secure upload URLs for ${files.length} documents...`);
  const presignedResponse = await getBatchUploadUrls(files);

  if (isCancelled?.()) throw new Error('Extraction cancelled');

  // Step 2: Upload all files to S3 in parallel
  onStatusChange?.(`Uploading ${files.length} invoice documents to S3...`);
  const progressArr = new Array(files.length).fill(0);
  await Promise.all(
    files.map((file, idx) => {
      const presigned = presignedResponse.files[idx];
      return uploadToS3(presigned, file, pct => {
        progressArr[idx] = pct;
        if (onUploadProgress) {
          const avg = Math.round(progressArr.reduce((a, b) => a + b, 0) / files.length);
          onUploadProgress(avg);
        }
      });
    }),
  );

  if (isCancelled?.()) throw new Error('Extraction cancelled');

  // Step 3: Trigger batch extraction
  onStatusChange?.(`Submitting ${files.length} invoice documents for batch AI extraction...`);
  const batchKeys = presignedResponse.files.map(f => f.key || f.fields?.['key'] || '');
  await startBatchVendorInvoiceExtraction(presignedResponse.batchId, batchKeys);

  // Step 4: Polling loop
  let currentStatus = 'pending';
  let batchStatusResult: BatchExtractionStatusResponse | null = null;
  let isBatchInProgress = true;

  while (isBatchInProgress) {
    if (isCancelled?.()) throw new Error('Extraction cancelled');

    onStatusChange?.(
      currentStatus === 'processing'
        ? `AI is analyzing ${files.length} invoice documents... (processing)`
        : `AI batch extraction in progress for ${files.length} documents... (waiting for results)`,
    );

    // Wait poll interval before the poll without overlapping requests
    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        registerTimer?.(null);
        if (isCancelled?.()) {
          reject(new Error('Extraction cancelled'));
        } else {
          resolve();
        }
      }, pollIntervalMs);
      registerTimer?.(timer);
    });

    if (isCancelled?.()) throw new Error('Extraction cancelled');

    onStatusChange?.('Checking batch extraction status...');
    batchStatusResult = await getBatchVendorInvoiceExtractionStatus(presignedResponse.batchId);

    const status = (batchStatusResult?.status || '').toLowerCase();
    const counts = batchStatusResult?.counts;
    const pendingCount = counts ? counts.pending ?? 0 : null;

    currentStatus = status || 'processing';

    const isTerminalStatus =
      status === 'completed' || status === 'success' || status === 'failed' || status === 'error';
    const isAllCountsSettled = pendingCount !== null && pendingCount === 0;

    if (isTerminalStatus || isAllCountsSettled) {
      isBatchInProgress = false;
      break;
    }
  }

  // Aggregate results from batch jobs
  const jobs = batchStatusResult?.jobs || [];
  const allShipments: any[] = [];
  let completedCount = 0;
  let reviewCount = 0;
  let failedCount = 0;
  const errorMessages: string[] = [];

  for (const job of jobs) {
    const jobStatus = (job.status || '').toLowerCase();
    if (jobStatus === 'completed' || jobStatus === 'needs_review') {
      if (jobStatus === 'completed') completedCount++;
      if (jobStatus === 'needs_review') reviewCount++;
      if (job.fields) {
        const jobShipments = mapAiInvoicesToShipments(job.fields);
        if (Array.isArray(jobShipments)) {
          allShipments.push(...jobShipments);
        }
      }
    } else if (jobStatus === 'failed') {
      failedCount++;
      const err =
        typeof job.error === 'string' ? job.error : job.error?.message || `Extraction failed for one or more files`;
      errorMessages.push(err);
    }
  }

  // Fallback if top-level fields exist
  if (allShipments.length === 0 && (batchStatusResult as any)?.fields) {
    allShipments.push(...mapAiInvoicesToShipments((batchStatusResult as any).fields));
  }

  const finalStatus = batchStatusResult?.status || currentStatus;
  const hasExtractedData = allShipments.length > 0;

  return {
    batchId: presignedResponse.batchId,
    status: finalStatus,
    counts: batchStatusResult?.counts,
    jobs: batchStatusResult?.jobs,
    shipments: allShipments,
    successfulCount: completedCount + reviewCount,
    failedCount,
    error: !hasExtractedData
      ? errorMessages.join('; ') ||
        (batchStatusResult as any)?.message ||
        (batchStatusResult as any)?.error ||
        'Batch extraction failed to return shipment data'
      : null,
  };
};

export const toNumber = (value: any): number => {
  if (value === null || value === undefined || value === '') return 0;

  if (typeof value === 'string') {
    const cleaned = value.replace(/[$,%]/g, '').replace(/,/g, '').trim();
    const num = parseFloat(cleaned);
    return isNaN(num) ? 0 : num;
  }

  const num = Number(value);
  return isNaN(num) ? 0 : num;
};

/**
 * Map AI extraction response fields into flat ShipmentInvoice array
 */
export const mapAiInvoicesToShipments = (fields: any): any[] => {
  const allShipments: any[] = [];
  if (!fields) return allShipments;

  const rawInvoices = fields.invoices || fields.shipments || [];

  if (!Array.isArray(rawInvoices)) return allShipments;

  for (const item of rawInvoices) {
    // Case 1: Grouped format ({ carrierReferenceNumber, shipments: [...] })
    if (Array.isArray(item.shipments)) {
      const carrierRef = (item.carrierReferenceNumber || '').toString().trim();

      for (const s of item.shipments) {
        const isTaxedValue =
          typeof s.isTaxed === 'boolean'
            ? s.isTaxed
            : typeof s.isTaxed === 'string'
            ? s.isTaxed.trim().toLowerCase() === 'yes' || s.isTaxed.trim().toLowerCase() === 'true'
            : false;

        const charges = Array.isArray(s.charges)
          ? s.charges
              .map((c: any) => ({
                name: String(c.name || '').trim(),
                amount: toNumber(c.amount),
              }))
              .filter((c: any) => c.name && c.amount !== 0)
          : [];

        const shipmentObj = {
          shipmentNumber: formatShipmentNumber(s.shipmentNumber) || '',
          awbNumber: s.awbNumber ? String(s.awbNumber).trim() : '',
          unit: s.unit ? String(s.unit).trim() : 'kg-cm',
          isTaxed: isTaxedValue,
          actualWeight: toNumber(s.actualWeight),
          chargeableWeight: toNumber(s.chargeableWeight),
          length: toNumber(s.length ?? s.volumetricWeight),
          breadth: toNumber(s.breadth),
          height: toNumber(s.height),
          weight: toNumber(s.weight),
          numberOfBoxes: toNumber(s.numberOfBoxes) || 1,
          carrierReferenceNumber:
            carrierRef || (s.carrierReferenceNumber ? String(s.carrierReferenceNumber).trim() : ''),
          charges,
        };

        allShipments.push(shipmentObj);
      }
    } else {
      // Case 2: Flat shipment format ({ shipmentNumber, awbNumber, carrierReferenceNumber, ... })
      const isTaxedValue =
        typeof item.isTaxed === 'boolean'
          ? item.isTaxed
          : typeof item.isTaxed === 'string'
          ? item.isTaxed.trim().toLowerCase() === 'yes' || item.isTaxed.trim().toLowerCase() === 'true'
          : false;

      const charges = Array.isArray(item.charges)
        ? item.charges
            .map((c: any) => ({
              name: String(c.name || '').trim(),
              amount: toNumber(c.amount),
            }))
            .filter((c: any) => c.name && c.amount !== 0)
        : [];

      const shipmentObj = {
        shipmentNumber: formatShipmentNumber(item.shipmentNumber) || '',
        awbNumber: item.awbNumber ? String(item.awbNumber).trim() : '',
        unit: item.unit ? String(item.unit).trim() : 'kg-cm',
        isTaxed: isTaxedValue,
        actualWeight: toNumber(item.actualWeight),
        chargeableWeight: toNumber(item.chargeableWeight),
        length: toNumber(item.length ?? item.volumetricWeight),
        breadth: toNumber(item.breadth),
        height: toNumber(item.height),
        weight: toNumber(item.weight),
        numberOfBoxes: toNumber(item.numberOfBoxes) || 1,
        carrierReferenceNumber: item.carrierReferenceNumber ? String(item.carrierReferenceNumber).trim() : '',
        charges,
      };

      allShipments.push(shipmentObj);
    }
  }

  return allShipments;
};
